<template>
  <div>
    <div v-if="groupedBy" class="rb-note">
      <span class="material-icons-round">info</span>
      <span>{{ groupedBy }}</span>
    </div>
    <div v-if="result && result.truncated" class="rb-note rb-note-warn">
      {{ $t("rb_truncated", { n: Number(result.limit).toLocaleString() }) }}
    </div>
    <div v-if="result && result.rows.length" class="rb-table-wrap">
      <table class="table align-items-center mb-0 rb-table">
        <thead>
          <tr>
            <th
              v-for="(col, i) in result.columns"
              :key="i"
              :class="{ 'text-end': col.type === 'number' }"
            >
              <button
                v-if="sortable"
                type="button"
                class="rb-th-btn"
                :title="$t('rb_sort_by', { name: col.name })"
                @click="$emit('sort', i)"
              >
                {{ col.name }}<span v-if="sort && sort.column === i">{{ sort.desc ? " ↓" : " ↑" }}</span>
              </button>
              <span v-else>{{ col.name }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, r) in result.rows" :key="r">
            <td
              v-for="(value, i) in row"
              :key="i"
              :class="{ 'text-end rb-num': result.columns[i].type === 'number' }"
            >{{ format(value, result.columns[i]) }}</td>
          </tr>
        </tbody>
        <tfoot v-if="totals">
          <tr class="rb-total">
            <td
              v-for="(value, i) in totals"
              :key="i"
              :class="{ 'text-end rb-num': result.columns[i].type === 'number' }"
            >
              {{ i === 0 && value === null ? $t("rb_total") : format(value, result.columns[i]) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div v-else-if="result" class="rb-empty">{{ $t("rb_no_rows") }}</div>
  </div>
</template>

<script>
import { formatCell } from "./reportBuilder";

export default {
  name: "ReportResultTable",
  props: {
    result: { type: Object, default: null },
    sort: { type: Object, default: null },
    sortable: { type: Boolean, default: false },
  },
  emits: ["sort"],
  computed: {
    grouped() {
      return !!this.result && this.result.columns.some((c) => c.agg);
    },
    groupedBy() {
      if (!this.grouped) return "";
      const plain = this.result.columns.filter((c) => !c.agg).map((c) => c.name);
      return plain.length
        ? this.$t("rb_grouped_by", { cols: plain.join(", ") })
        : this.$t("rb_one_row");
    },
    // A total row on screen only (the CSV keeps the spec's format), for totals and counts,
    // and only when every row is here.
    totals() {
      if (!this.grouped || this.result.truncated || this.result.rows.length < 2) return null;
      const summable = this.result.columns.map((c) => c.agg === "sum" || c.agg === "count");
      if (!summable.some(Boolean)) return null;
      return this.result.columns.map((c, i) => {
        if (!summable[i]) return null;
        const sum = this.result.rows.reduce((s, r) => s + Number(r[i] || 0), 0);
        return Math.round(sum * 100) / 100;
      });
    },
  },
  methods: {
    format(value, column) {
      return formatCell(value, column);
    },
  },
};
</script>

<style scoped>
.rb-table-wrap {
  overflow: auto;
  max-height: 620px;
}
.rb-table th {
  /* Column names are the spec's labels: shown exactly as written. */
  text-transform: none !important;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  font-size: 0.75rem;
  white-space: nowrap;
}
.rb-table td {
  font-size: 0.8rem;
  white-space: nowrap;
}
.rb-num {
  font-variant-numeric: tabular-nums;
}
.rb-th-btn {
  border: 0;
  background: none;
  padding: 0;
  font-weight: 700;
}
.rb-total td {
  font-weight: 700;
  background: #f5f7f7;
  position: sticky;
  bottom: 0;
}
.rb-note {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 8px 12px;
  font-size: 0.8rem;
  background: #f5f7f7;
  border-radius: 6px;
  margin-bottom: 8px;
}
.rb-note .material-icons-round {
  font-size: 1rem;
}
.rb-note-warn {
  background: #fff3d6;
  color: #7a4e00;
}
.rb-empty {
  padding: 32px 12px;
  text-align: center;
  color: #6c757d;
}
</style>
